import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Sparkles,
  Send,
  User,
  Bot,
  RefreshCw,
  Copy,
  ThumbsUp,
  ThumbsDown,
  Loader2,
  MessageSquare,
  Target,
  Shield,
  TrendingUp,
  FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const modeConfig = {
  credit_coach: {
    icon: Target,
    label: 'Credit Coach',
    description: 'Get advice on improving your credit',
    color: 'emerald'
  },
  dispute_coach: {
    icon: Shield,
    label: 'Dispute Coach',
    description: 'Strategy for disputing items',
    color: 'amber'
  },
  funding_coach: {
    icon: TrendingUp,
    label: 'Funding Coach',
    description: 'Prepare for loan applications',
    color: 'emerald'
  },
  report_explainer: {
    icon: FileText,
    label: 'Report Explainer',
    description: 'Understand your credit report',
    color: 'amber'
  }
};

const suggestedQuestions = [
  "What's hurting my credit score the most?",
  "Should I dispute this collection account?",
  "How can I improve my utilization?",
  "What does this tradeline mean?",
  "Am I ready for a business loan?",
  "How long will negative items stay on my report?"
];

// Message Component
const Message = ({ message, isUser }) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    toast.success('Copied to clipboard');
  };

  return (
    <div className={cn(
      "flex gap-3 p-4",
      isUser ? "bg-slate-50" : "bg-white"
    )}>
      <div className={cn(
        "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
        isUser ? "bg-emerald-100" : "bg-amber-100"
      )}>
        {isUser ? (
          <User size={16} className="text-emerald-600" />
        ) : (
          <Sparkles size={16} className="text-amber-600" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-stone-900 text-sm">
            {isUser ? 'You' : 'FundElite AI'}
          </span>
          {!isUser && (
            <span className="text-xs px-2 py-0.5 bg-amber-50 text-amber-600 rounded-full">
              {modeConfig[message.mode]?.label || 'AI'}
            </span>
          )}
        </div>
        <div className="text-slate-700 whitespace-pre-wrap text-sm leading-relaxed">
          {message.content}
        </div>
        {!isUser && (
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={handleCopy}
              className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600 transition-colors"
            >
              <Copy size={14} />
            </button>
            <button className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600 transition-colors">
              <ThumbsUp size={14} />
            </button>
            <button className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600 transition-colors">
              <ThumbsDown size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Mode Selector
const ModeSelector = ({ mode, onModeChange }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
      {Object.entries(modeConfig).map(([key, config]) => {
        const Icon = config.icon;
        const isActive = mode === key;
        
        return (
          <button
            key={key}
            onClick={() => onModeChange(key)}
            className={cn(
              "p-3 rounded-xl border transition-all text-left",
              isActive 
                ? "border-emerald-500 bg-emerald-50" 
                : "border-slate-200 hover:border-slate-300 bg-white"
            )}
            data-testid={`mode-${key}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Icon size={16} className={isActive ? "text-emerald-600" : "text-slate-500"} />
              <span className={cn(
                "font-medium text-sm",
                isActive ? "text-emerald-700" : "text-slate-700"
              )}>
                {config.label}
              </span>
            </div>
            <p className="text-xs text-slate-500">{config.description}</p>
          </button>
        );
      })}
    </div>
  );
};

// Main AI Assistant Component
const AIAssistant = () => {
  const { getAuthHeaders } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [mode, setMode] = useState('credit_coach');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      id: Date.now(),
      content: input.trim(),
      isUser: true,
      mode
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai/chat`,
        { message: input.trim(), mode },
        { headers: getAuthHeaders(), withCredentials: true }
      );

      const aiMessage = {
        id: Date.now() + 1,
        content: response.data.response,
        isUser: false,
        mode: response.data.mode
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('AI chat error:', error);
      toast.error('Failed to get AI response. Please try again.');
      
      // Add error message
      const errorMessage = {
        id: Date.now() + 1,
        content: "I apologize, but I encountered an error. Please try again or rephrase your question.",
        isUser: false,
        mode
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedQuestion = (question) => {
    setInput(question);
    inputRef.current?.focus();
  };

  const handleClearChat = () => {
    setMessages([]);
  };

  return (
    <div className="h-[calc(100vh-180px)] flex flex-col" data-testid="ai-assistant">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-emerald-500 flex items-center justify-center">
            <Sparkles size={24} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-stone-900">AI Assistant</h1>
            <p className="text-sm text-slate-500">Your personal credit coach</p>
          </div>
        </div>
        {messages.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearChat}
            className="text-slate-500"
          >
            <RefreshCw size={16} className="mr-1" />
            Clear Chat
          </Button>
        )}
      </div>

      {/* Mode Selector */}
      <ModeSelector mode={mode} onModeChange={setMode} />

      {/* Messages Area */}
      <div className="flex-1 elite-card overflow-hidden flex flex-col">
        {messages.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center max-w-md">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-100 to-emerald-100 flex items-center justify-center">
                <MessageSquare size={28} className="text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-stone-900 mb-2">
                How can I help you today?
              </h3>
              <p className="text-sm text-slate-500 mb-6">
                Ask me anything about credit repair, disputes, utilization, or funding readiness.
              </p>
              
              <div className="space-y-2">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Suggested Questions
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {suggestedQuestions.slice(0, 4).map((q, i) => (
                    <button
                      key={i}
                      onClick={() => handleSuggestedQuestion(q)}
                      className="px-3 py-2 text-sm bg-slate-50 hover:bg-slate-100 rounded-lg text-slate-600 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {messages.map((message) => (
              <Message key={message.id} message={message} isUser={message.isUser} />
            ))}
            {loading && (
              <div className="flex gap-3 p-4 bg-white">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Sparkles size={16} className="text-amber-600" />
                </div>
                <div className="flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin text-amber-600" />
                  <span className="text-sm text-slate-500">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Input Area */}
        <div className="border-t p-4 bg-white">
          <div className="flex gap-3">
            <Textarea
              ref={inputRef}
              placeholder="Ask about your credit, disputes, or funding..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="min-h-[44px] max-h-32 resize-none"
              disabled={loading}
              data-testid="ai-input"
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              className="btn-ai px-4"
              data-testid="ai-send-btn"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Send size={18} />
              )}
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-2 text-center">
            AI responses are for educational purposes. Always verify with professionals.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
